// Makes the view count on a premium owner's own profile work cards a link to
// their analytics readers page — the report that says who those views were.
//
// ## Why this is not simply an <a> in the partial
//
// Whether to link is a question about the VIEWER, and the card is not. The
// paginated work card is fragment-cached by Caches::WowWorkStrip with no viewer
// in its key (work id, updated_at, last_author_update, asset digests), so a
// Ruby conditional inside it would bake whichever viewer rendered first into
// the shared fragment and serve it to everybody. The page answers the viewer
// question once, as a body class — see
// Profiles::ProfileController#show_view_count_readers_link? — and this reads it.
//
// ## Why it wraps rather than binding a click handler
//
// The count is a navigation, so it should behave like one: middle-click and
// cmd-click open a tab, it takes focus in tab order, and a screen reader
// announces a link. A click listener on a <span> gives none of that. The span
// itself is left in place INSIDE the new anchor, because the async view-count
// fetcher (components/works/_view_count) finds it by .js-view-count and writes
// the number in long after this runs.

import { readersPageUrl } from 'js/profiles/readersPageUrl';

// Set on <body> by Profiles::ProfileController. Kept in step with
// VIEW_COUNT_READERS_LINK_CLASS there.
export const PAGE_GATE_CLASS = 'view-count-links-to-readers';

// Scoped to WORK view counts: _video_strip renders the same js- class keyed by
// data-video-id, and the readers report has nothing to say about a video.
const VIEW_COUNT_SELECTOR = '.js-view-count[data-work-id]';

const LINK_CLASS = 'view-count-readers-link';

// Set on the count, not the anchor, so a second pass over an already-wrapped
// card is a no-op however it is reached — the initial scan and the observer
// below can both see the same node.
const WRAPPED_FLAG = 'viewCountReadersLinked';

const wrap = (count: HTMLElement, href: string): void => {
  if (count.dataset[WRAPPED_FLAG] === '1') return;
  count.dataset[WRAPPED_FLAG] = '1';

  const parent = count.parentNode;
  if (parent == null) return;

  const link = document.createElement('a');
  link.href = href;
  link.className = LINK_CLASS;

  parent.insertBefore(link, count);
  link.appendChild(count);
};

const wrapAll = (root: ParentNode, href: string): void => {
  root
    .querySelectorAll<HTMLElement>(VIEW_COUNT_SELECTOR)
    .forEach((count) => wrap(count, href));
};

export const setUpViewCountReadersLink = (): void => {
  if (!document.body.classList.contains(PAGE_GATE_CLASS)) return;

  const href = readersPageUrl();
  if (href == null) return;

  wrapAll(document, href);

  // Infinite scroll appends work cards long after DOMContentLoaded, so a
  // one-shot scan would leave every card past the first page unlinked. An
  // observer rather than a delegated listener because the wrapping has to
  // happen before the member interacts with the card, not in response to it.
  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;

        // The count can be the appended node itself or sit inside it,
        // depending on how much of the card was built off-document.
        if (node.matches(VIEW_COUNT_SELECTOR)) wrap(node, href);
        wrapAll(node, href);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

export default setUpViewCountReadersLink;
